// NodeMissionMeta.jsx

import React, { useEffect, useState } from 'react';

// Utilities
import {
  resolveMissionElapsedPresentation,
  resolveMissionEndDatePresentation,
} from 'missions/scheduling/missionScheduleUtils';

// CSS
import './NodeMissionMeta.css';

/**
 * NodeMissionMeta
 *
 * Progress + elapsed on the left; estimated / finished end date on the right.
 *
 * @param {boolean} hasRun - Whether the Mission has a run.
 * @param {object} missionProgress - Mission completion progress.
 * @param {string} [missionStatus] - Derived run status key.
 * @param {object} template - Mission template.
 * @param {object} missionRun - Mission run.
 *
 * @returns {ReactNode} - NodeMissionMeta.
 */
const NodeMissionMeta = React.memo(({
  hasRun,
  missionProgress,
  missionStatus = '',
  template,
  missionRun,
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!hasRun || missionStatus === 'complete') return undefined;

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 30_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasRun, missionStatus]);

  const elapsedPresentation = hasRun
    ? resolveMissionElapsedPresentation({
      missionRun,
      missionStatus,
      nowMs,
    })
    : null;

  const endDatePresentation = hasRun
    ? resolveMissionEndDatePresentation({
      missionRun,
      missionStatus,
      nowMs,
    })
    : null;

  // ----- RENDER ----- //

  return (
    <div className="node-mission-meta-container">

      {/* META ITEM — progress */}
      <div className="node-mission-meta-item">
        <div className="node-mission-meta-value">
          {hasRun
            ? `${missionProgress.complete} of ${missionProgress.total || 0} tasks complete`
            : `${template.tasks.length} task${template.tasks.length === 1 ? '' : 's'} ready to launch`}
        </div>
        {elapsedPresentation?.secondary ? (
          <div className="node-mission-meta-secondary node-mission-meta-secondary--start">
            {elapsedPresentation.secondary}
          </div>
        ) : null}
      </div>

      {/* META ITEM — estimated / finished end date */}
      <div className="node-mission-meta-item node-mission-meta-item--end">
        {endDatePresentation ? (
          <>
            <div className="node-mission-meta-value">
              {endDatePresentation.primary}
            </div>
            {endDatePresentation.secondary ? (
              <div className="node-mission-meta-secondary">
                {endDatePresentation.secondary}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

    </div>
  );
});

// Dev Tools
NodeMissionMeta.displayName = 'NodeMissionMeta';

export default NodeMissionMeta;
